import { css } from '@emotion/react';
import React from 'react';
import { useSelector } from 'react-redux';
import * as humps from 'humps';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import {
  generateLearnerFinalSummary,
  getLearnerFinalSummaryDetails,
  setLearnerFinalSummaryUpdated,
} from 'redux/actions/help-options';
import { RootState } from 'redux/schemas';
import {
  LearnerFinalSummaryHighlightSection,
  LearnerFinalSummaryStatus,
  LearnerFinalSummaryUpdatedEventData,
} from 'redux/schemas/models/help-options';
import {
  getLearnerFinalSummary,
} from 'redux/selectors/help-options';
import { getCurrentUser } from 'redux/selectors/users';
import { useAppDispatch } from 'redux/store';
import pusherService from 'shared/services/pusher-service';
import {
  genAIGradientType4,
  gray6,
  info,
} from 'styles/global_defaults/colors';
import { headerLineHeight as textRegularLargeLineHeight } from 'styles/global_defaults/fonts';
import {
  halfSpacing,
  largeSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';

type PersonalizedTakeawaysAccessProps = {
  shouldShow: boolean;
};

export const LEARNER_FINAL_SUMMARY_UPDATED_EVENT = 'learner_final_summary_updated';

const takeawaysSurface = info;

const styles = css`
  background: ${takeawaysSurface};
  border-top: 1px solid ${gray6};
  padding: ${largeSpacing}px;

  .takeaways-header-content {
    padding: 0 ${largeSpacing}px;
  }

  .takeaways-heading {
    .takeaways-ai-icon {
      margin-right: ${standardSpacing}px;
    }

    .takeaways-description {
      margin-bottom: ${standardSpacing}px;
    }
  }

  .takeaways-gradient-border {
    background: linear-gradient(${takeawaysSurface}, ${takeawaysSurface}) padding-box,
      ${genAIGradientType4} border-box;
    border: 2px solid transparent;
    color: inherit;
    cursor: pointer;
  }

  .takeaways-button {
    width: 100%;
    margin-top: ${standardSpacing}px;
    border-radius: ${standardSpacing}px;
    padding: ${standardSpacing}px ${largeSpacing}px;
    text-align: left;

    .takeaways-card-content {
      width: 100%;
    }

    .takeaways-button-label {
      margin-bottom: ${halfSpacing}px;
    }

    .takeaways-arrow {
      margin-left: ${standardSpacing}px;
    }

  }

  .takeaways-text-regular-large {
    line-height: ${textRegularLargeLineHeight}px;
  }

  .takeaways-generate-button {
    margin-right: ${largeSpacing}px;
    border-radius: ${standardSpacing}px;
    height: 40px;
    padding: 0 ${largeSpacing}px;

    .takeaways-ai-icon {
      margin-right: ${halfSpacing}px;
    }
  }

`;

const PersonalizedTakeawaysAccess = ({ shouldShow }: PersonalizedTakeawaysAccessProps) => {
  const { injectServices } = React.useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const dispatch = useAppDispatch();
  const [isGeneratingRequest, setIsGeneratingRequest] = React.useState(false);
  const requestedSummaryIdRef = React.useRef<number | null>(null);
  const catalogId = useSelector((state: RootState) => state.app.currentCatalogId);
  const courseId = useSelector((state: RootState) => state.app.currentCourseId);
  const currentUser = useSelector(getCurrentUser);
  const learnerFinalSummary = useSelector((state: RootState) => getLearnerFinalSummary(state, currentUser?.id));

  const learnerFinalSummaryStatus = learnerFinalSummary?.status || LearnerFinalSummaryStatus.FAILED;
  const isSummaryGenerating = learnerFinalSummaryStatus === LearnerFinalSummaryStatus.QUEUED
    || learnerFinalSummaryStatus === LearnerFinalSummaryStatus.IN_PROGRESS;
  const shouldShowGenerateNowButton = !(learnerFinalSummary?.available || learnerFinalSummary?.status === LearnerFinalSummaryStatus.COMPLETED)
    && !isSummaryGenerating;
  const atAGlanceHeadline = learnerFinalSummary?.highlightSections
    ?.find((section: LearnerFinalSummaryHighlightSection) => section.sectionType === 'at_a_glance')
    ?.sectionPayload?.content?.headline || '';

  React.useEffect(() => {
    if (!courseId || !currentUser?.id || !isSummaryGenerating) {
      return () => {};
    }

    const pusherChannel = pusherService.initializeCourseChannel(courseId);
    if (!pusherChannel) {
      return () => {};
    }

    const handleLearnerFinalSummaryUpdated = (pusherData: unknown) => {
      const data = humps.camelizeKeys(pusherData as Record<string, unknown>) as LearnerFinalSummaryUpdatedEventData;

      if (data.userId !== currentUser.id) {
        return;
      }

      dispatch(setLearnerFinalSummaryUpdated({
        learnerFinalSummaryReportId: data.learnerFinalSummaryReportId,
        userId: currentUser.id,
        status: data.status,
        available: data.available,
        generatedAt: data.generatedAt,
        errorMessage: data.errorMessage,
      }));
    };

    pusherChannel.bind(LEARNER_FINAL_SUMMARY_UPDATED_EVENT, handleLearnerFinalSummaryUpdated);

    return () => {
      pusherChannel.unbind(LEARNER_FINAL_SUMMARY_UPDATED_EVENT, handleLearnerFinalSummaryUpdated);
    };
  }, [courseId, currentUser?.id, dispatch, isSummaryGenerating]);

  React.useEffect(() => {
    if (!shouldShow || !catalogId || !currentUser?.id || !learnerFinalSummary?.available || !learnerFinalSummary?.id) {
      return;
    }

    if (requestedSummaryIdRef.current === learnerFinalSummary.id) {
      return;
    }

    requestedSummaryIdRef.current = learnerFinalSummary.id;

    dispatch(getLearnerFinalSummaryDetails({
      catalogId,
      userId: currentUser.id,
      learnerFinalSummaryReportId: learnerFinalSummary.id,
    }));
  }, [
    shouldShow,
    catalogId,
    currentUser?.id,
    learnerFinalSummary?.available,
    learnerFinalSummary?.id,
    dispatch,
  ]);

  const onGenerateClick = () => {
    if (!catalogId || !currentUser?.id || isGeneratingRequest) {
      return;
    }

    setIsGeneratingRequest(true);

    dispatch(generateLearnerFinalSummary({
      catalogId,
      userId: currentUser.id,
    })).finally(() => {
      setIsGeneratingRequest(false);
    });
  };

  const onOpenTakeawaysPage = () => {
    if (!catalogId) {
      return;
    }

    $state.go('course-personalized-takeaways', {
      catalogId,
      from: $state.current.name,
      learnerFinalSummaryReportId: learnerFinalSummary?.id,
    });
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <section css={styles}>
      <div className='takeaways-header-row d-flex align-items-center justify-content-between'>
        <div className='takeaways-header-content takeaways-heading d-flex align-items-center mb-0'>
          <GradientAiIcon icon='gen-ai' size='large' className='takeaways-ai-icon' />
          <div>
            <div className='text-large-body takeaways-text-regular-large font-weight-bolder'>
              {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.HEADER()}
            </div>
            <p className='takeaways-description text-large-body takeaways-text-regular-large mb-0'>
              {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.SUBTITLE()}
            </p>
          </div>
        </div>

        {shouldShowGenerateNowButton && (
          <button
            type='button'
            className='takeaways-gradient-border takeaways-generate-button d-flex align-items-center'
            disabled={isGeneratingRequest}
            onClick={onGenerateClick}
          >
            <GradientAiIcon icon='gen-ai' size='small' className='takeaways-ai-icon' />
            <div className='takeaways-button-label text-regular font-weight-bolder mb-0'>
              {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.GENERATE_NOW()}
            </div>
          </button>
        )}
      </div>

      {!shouldShowGenerateNowButton && (
        <button
          type='button'
          className='takeaways-gradient-border takeaways-button d-flex align-items-center'
          onClick={onOpenTakeawaysPage}
        >
          <div className='takeaways-card-content'>
            <div className='takeaways-button-label text-regular font-weight-bolder'>
              {t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.AT_A_GLANCE()}
            </div>
            <div className='text-regular text-gray-1 takeaways-message'>
              {isSummaryGenerating || (!atAGlanceHeadline || atAGlanceHeadline.trim() === '')
                ? t.COURSE_HOME.PERSONALIZED_TAKEAWAYS.GENERATING()
                : atAGlanceHeadline}
            </div>
          </div>
          <GradientAiIcon icon='expand' size='medium-large' className='takeaways-arrow' />
        </button>
      )}
    </section>
  );
};

export default PersonalizedTakeawaysAccess;
